import React from "react";
import {
  AbsoluteFill,
  Easing,
  interpolate,
  interpolateColors,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { FrakLogo } from "../../components/FrakLogo";
import { useCopy } from "../copy";
import { STORY_GAIN } from "../data/brands";
import { LOOK, SAFE, TYPE } from "../look";
import { useStage } from "../stage";
import { Words } from "../ui/Words";
import { Sfx } from "../../b2b/ui/Sfx";

const clamp = { extrapolateLeft: "clamp", extrapolateRight: "clamp" } as const;

/**
 * Scene 3 — the injustice, then the fix, on the same number. Built like a
 * punchline: set-up, a pause, the blow, a beat to let it sink in.
 *
 *   0        "Et pourtant, tu gagnes"
 *   DOTS     "…" typed one dot at a time (the pause)
 *   ZERO_AT  0,00 € slams in on the beat: impact, shake, colour drains
 *            out of the frame (grey = nothing happens for you)
 *   SWITCH   Frak arrives: flash, colour back, "Avec frak," and the same
 *            figure rolls to +5,00 € (green), then the explanation
 *
 * Keeping the figure in place makes the before/after read instantly.
 */
const DOTS = [16, 23, 30];
const ZERO_AT = 42; // beat 3
const SWITCH = 84; // beat 6

export const Unpaid: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const copy = useCopy();
  const stage = useStage();

  const isB = frame >= SWITCH;

  // --- Phase A: the blow --------------------------------------------------
  // Falls in from big to slightly-too-small (accelerating = impact), then
  // settles. Blur on the way in sells the speed.
  const slam = interpolate(frame, [ZERO_AT, ZERO_AT + 5, ZERO_AT + 10], [2.6, 0.93, 1], {
    ...clamp,
    easing: [Easing.in(Easing.cubic), Easing.out(Easing.quad)],
  });
  const slamBlur = interpolate(frame, [ZERO_AT, ZERO_AT + 5], [14, 0], clamp);
  const zeroOpacity = interpolate(frame, [ZERO_AT, ZERO_AT + 2], [0, 1], clamp);
  // Then it slowly deflates while it sinks in.
  const deflate = interpolate(frame, [ZERO_AT + 14, SWITCH], [1, 0.95], clamp);
  const sag = interpolate(frame, [ZERO_AT + 14, SWITCH], [0, -2.5], clamp);

  // Whole-frame shake on impact, decaying over ~12f.
  const t = frame - ZERO_AT - 5;
  const shake = t >= 0 && t < 12 ? Math.sin(t * 2.3) * (12 - t) * 1.6 : 0;

  // --- Phase B: Frak ------------------------------------------------------
  const value = interpolate(frame, [SWITCH + 4, SWITCH + 22], [0, STORY_GAIN], {
    ...clamp,
    easing: Easing.bezier(0.16, 1, 0.3, 1),
  });
  const bump = spring({
    frame: frame - SWITCH - 4,
    fps,
    config: { damping: 9, stiffness: 260, mass: 0.6 },
  });
  const logo = spring({
    frame: frame - SWITCH,
    fps,
    config: { damping: 12, stiffness: 220, mass: 0.6 },
  });
  const flash = interpolate(frame, [SWITCH - 1, SWITCH, SWITCH + 8], [0, 0.85, 0], clamp);

  // Colour drains on the blow, comes back with Frak.
  const background = interpolateColors(
    frame,
    [ZERO_AT + 3, ZERO_AT + 9, SWITCH - 1, SWITCH],
    [LOOK.sky, LOOK.greyBg, LOOK.greyBg, LOOK.sky],
  );
  const amountColor = isB
    ? interpolateColors(frame, [SWITCH + 2, SWITCH + 8], [LOOK.inkSoft, LOOK.green])
    : LOOK.inkSoft;

  const amountText = isB ? `+${copy.money(value)}` : copy.unpaid.zero;
  const amountScale = isB ? interpolate(bump, [0, 1], [0.85, 1]) : slam * deflate;

  return (
    <AbsoluteFill style={{ background }}>
      <AbsoluteFill
        style={{
          alignItems: "center",
          justifyContent: "center",
          padding: SAFE,
          translate: `${shake}px ${shake * 0.4}px`,
        }}
      >
        {/* Lead line */}
        <div style={{ height: 120, display: "flex", alignItems: "center" }}>
          {!isB ? (
            <div style={{ display: "flex", alignItems: "flex-end" }}>
              <Words text={copy.unpaid.lead} fontSize={TYPE.title} />
              <Dots fontSize={TYPE.title} />
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 26 }}>
              <Words text={copy.unpaid.withFrak} start={SWITCH} fontSize={TYPE.title} />
              <div
                style={{
                  scale: logo,
                  rotate: `${(1 - logo) * -12}deg`,
                  marginTop: 10,
                }}
              >
                <FrakLogo width={260} />
              </div>
            </div>
          )}
        </div>

        {/* The figure */}
        <div
          style={{
            fontFamily: LOOK.display,
            fontWeight: LOOK.bold,
            fontSize: 240,
            lineHeight: 1,
            letterSpacing: -10,
            color: amountColor,
            fontVariantNumeric: "tabular-nums",
            margin: "12px 0 20px",
            scale: amountScale,
            rotate: isB ? "0deg" : `${sag}deg`,
            opacity: isB ? 1 : zeroOpacity,
            filter: !isB && slamBlur > 0.1 ? `blur(${slamBlur}px)` : undefined,
          }}
        >
          {amountText}
        </div>

        {/* After line */}
        <div style={{ height: 150 }}>
          {isB ? (
            <Words
              text={copy.unpaid.after}
              start={SWITCH + 14}
              stagger={2}
              fontSize={TYPE.support + 4}
              lineHeight={1.1}
              style={{ maxWidth: stage.shape === "wide" ? 1200 : 820 }}
            />
          ) : null}
        </div>
      </AbsoluteFill>

      <div
        style={{
          position: "absolute",
          bottom: 64 + stage.safeBottom,
          width: "100%",
          textAlign: "center",
          fontFamily: LOOK.body,
          fontSize: TYPE.legal,
          color: LOOK.inkSoft,
          opacity: interpolate(frame, [SWITCH + 30, SWITCH + 38], [0, 0.8], clamp),
        }}
      >
        {copy.unpaid.footnote}
      </div>

      {/* Frak's arrival flash */}
      <AbsoluteFill style={{ background: LOOK.white, opacity: flash, pointerEvents: "none" }} />

      {DOTS.map((at) => (
        <Sfx key={at} sound="key" at={at} volume={0.8} />
      ))}
      <Sfx sound="negative" at={ZERO_AT + 2} />
      <Sfx sound="slide" at={SWITCH} volume={0.8} />
      <Sfx sound="coins" at={SWITCH + 10} />
    </AbsoluteFill>
  );
};

/** "…" typed one dot per DOTS frame — the pause before the punchline. */
const Dots: React.FC<{ fontSize: number }> = ({ fontSize }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  return (
    <div
      style={{
        display: "flex",
        fontFamily: LOOK.display,
        fontWeight: LOOK.bold,
        fontSize,
        lineHeight: 1.05,
        color: LOOK.ink,
        marginLeft: 2,
        // Match Words' mask padding so the dots sit on the same baseline.
        paddingBottom: "0.14em",
        marginBottom: "-0.14em",
      }}
    >
      {DOTS.map((at) => {
        const p = spring({
          frame: frame - at,
          fps,
          config: { damping: 10, stiffness: 300, mass: 0.4 },
        });
        return (
          <span
            key={at}
            style={{
              display: "inline-block",
              scale: p,
              opacity: p > 0.01 ? 1 : 0,
              translate: `0 ${(1 - p) * -18}px`,
            }}
          >
            .
          </span>
        );
      })}
    </div>
  );
};
